export const requestIdleCallback = typeof window.requestIdleCallback === "function" ? (cb, opts) => window.requestIdleCallback(cb, opts) : (cb) => window.setTimeout(cb, 0);
export const yieldToMainThread = () => {
  if ("yield" in scheduler) {
    return scheduler.yield();
  }
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 0);
    });
  });
};
export function isLowPowerDevice() {
  return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
}
export function supportsViewTransitions() {
  return typeof document.startViewTransition === "function";
}
export function shouldDisableCrossDocumentViewTransitions(userAgent = navigator.userAgent) {
  const ua = userAgent || "";
  const androidWebView = /\bAndroid\b/i.test(ua) && /;\s?wv\)/i.test(ua);
  const knownInAppBrowser = /\b(FBAN|FBAV|FB_IAB|FBIOS|Instagram|musical_ly|Bytedance|BytedanceWebview|trill|TikTok)(?:\b|_)/i.test(ua);
  return androidWebView || knownInAppBrowser;
}
export const viewTransition = {
  current: void 0
};
const viewTransitionTypes = {
  "product-grid": async () => {
    const grid = document.querySelector(".product-grid");
    const productCards = (
      /** @type {HTMLElement[]} */
      [
        ...document.querySelectorAll(".product-grid .product-grid__item")
      ]
    );
    if (!grid || !productCards.length) return;
    await new Promise(
      (resolve) => requestIdleCallback(() => {
        const cardsToAnimate = getCardsToAnimate(grid, productCards);
        productCards.forEach((card, index) => {
          if (index < cardsToAnimate) {
            card.style.setProperty("view-transition-name", `product-card-${card.dataset.productId}`);
          } else {
            card.style.setProperty("content-visibility", "hidden");
          }
        });
        resolve(null);
      })
    );
    return () => productCards.forEach((card) => {
      card.style.removeProperty("view-transition-name");
      card.style.removeProperty("content-visibility");
    });
  }
};
export function startViewTransition(callback, types) {
  if (!supportsViewTransitions() || isLowPowerDevice() || prefersReducedMotion() || shouldDisableCrossDocumentViewTransitions()) {
    return Promise.resolve(callback());
  }
  return new Promise(async (resolve) => {
    let cleanupFunctions = [];
    if (types) {
      for (const type of types) {
        if (viewTransitionTypes[type]) {
          const cleanupFunction = await viewTransitionTypes[type]();
          if (cleanupFunction) cleanupFunctions.push(cleanupFunction);
        }
      }
    }
    const transition = document.startViewTransition(callback);
    if (!viewTransition.current) {
      viewTransition.current = transition.finished;
    }
    if (types) types.forEach((type) => transition.types.add(type));
    transition.finished.then(() => {
      viewTransition.current = void 0;
      cleanupFunctions.forEach((cleanupFunction) => cleanupFunction());
      resolve();
    });
  });
}
export function fetchConfig(type = "json", config = {}) {
  const headers = { "Content-Type": "application/json", Accept: `application/${type}`, ...config.headers };
  if (type === "javascript") {
    headers["X-Requested-With"] = "XMLHttpRequest";
    delete headers["Content-Type"];
  }
  return {
    method: "POST",
    headers: (
      /** @type {HeadersInit} */
      headers
    ),
    body: config.body
  };
}
export function debounce(fn, wait) {
  let timeout;
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }
  debounced.cancel = () => {
    clearTimeout(timeout);
  };
  return (
    /** @type {T & { cancel(): void }} */
    debounced
  );
}
export function throttle(fn, delay) {
  let lastCall = 0;
  function throttled(...args) {
    const now = performance.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  }
  throttled.cancel = () => {
    lastCall = performance.now();
  };
  return (
    /** @type {T & { cancel(): void }} */
    throttled
  );
}
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
export function prefersReducedMotion() {
  return reducedMotion.matches;
}
export function normalizeString(str) {
  return str.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}
export function onDocumentLoaded(callback) {
  if (document.readyState === "complete") {
    callback();
  } else {
    window.addEventListener("load", callback);
  }
}
export function onDocumentReady(callback, options) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, options);
  } else {
    callback();
  }
}
export function removeWillChangeOnAnimationEnd(event) {
  const target = event.target;
  if (target && target instanceof HTMLElement) {
    target.style.setProperty("will-change", "unset");
    target.removeEventListener("animationend", removeWillChangeOnAnimationEnd);
  }
}
export function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = Array.isArray(elements) ? elements.flatMap((element) => element.getAnimations(options)) : elements.getAnimations(options);
  const animationPromises = animations.reduce(
    (acc, animation) => {
      if (animation.timeline instanceof DocumentTimeline) {
        acc.push(animation.finished);
      }
      return acc;
    },
    /** @type {Promise<Animation>[]} */
    []
  );
  return Promise.allSettled(animationPromises).then(callback);
}
const scrollLockOwners = /* @__PURE__ */ new Set();
let scrollLockObserver;
function pruneDisconnectedScrollLockOwners() {
  for (const owner of scrollLockOwners) {
    if (!owner.isConnected) {
      scrollLockOwners.delete(owner);
    }
  }
}
function syncScrollLock() {
  pruneDisconnectedScrollLockOwners();
  if (scrollLockOwners.size > 0) {
    document.documentElement.setAttribute("scroll-lock", "");
    if (!scrollLockObserver) {
      scrollLockObserver = new MutationObserver(syncScrollLock);
      scrollLockObserver.observe(document.documentElement, { childList: true, subtree: true });
    }
    return;
  }
  document.documentElement.removeAttribute("scroll-lock");
  scrollLockObserver?.disconnect();
  scrollLockObserver = void 0;
}
export function lockScroll(owner) {
  scrollLockOwners.add(owner);
  syncScrollLock();
}
export function unlockScroll(owner) {
  scrollLockOwners.delete(owner);
  syncScrollLock();
}
export function isClickedOutside(event, element) {
  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }
  if (!event.target.isConnected) return !event.composedPath().includes(element);
  return !element.contains(event.target);
}
export function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();
  return x >= left && x <= right && y >= top && y <= bottom;
}
export const mediaQueryLarge = matchMedia("(min-width: 750px)");
export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}
export function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}
export function isTouchDevice() {
  return "ontouchstart" in window && navigator.maxTouchPoints > 0;
}
export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}
export function center(element, axis) {
  const { left, width, top, height } = element.getBoundingClientRect();
  const point = {
    x: left + width / 2,
    y: top + height / 2
  };
  if (axis) return (
    /**  @type {any} */
    point[axis]
  );
  return (
    /**  @type {any} */
    point
  );
}
export function start(element, axis) {
  const { left, top } = element.getBoundingClientRect();
  const point = { x: left, y: top };
  if (axis) return (
    /**  @type {any} */
    point[axis]
  );
  return (
    /**  @type {any} */
    point
  );
}
export function closest(values, target) {
  return values.reduce(function(prev, curr) {
    return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
  });
}
export function preventDefault(event) {
  event.preventDefault();
}
export function getVisibleElements(root, elements, ratio = 1, axis) {
  if (!elements?.length) return [];
  const rootRect = root.getBoundingClientRect();
  return elements.filter((element) => {
    const { width, height, top, right, left, bottom } = element.getBoundingClientRect();
    if (ratio < 1) {
      const intersectionLeft = Math.max(rootRect.left, left);
      const intersectionRight = Math.min(rootRect.right, right);
      const intersectionWidth = Math.max(0, intersectionRight - intersectionLeft);
      if (axis === "x") {
        return width > 0 && intersectionWidth / width >= ratio;
      }
      const intersectionTop = Math.max(rootRect.top, top);
      const intersectionBottom = Math.min(rootRect.bottom, bottom);
      const intersectionHeight = Math.max(0, intersectionBottom - intersectionTop);
      if (axis === "y") {
        return height > 0 && intersectionHeight / height >= ratio;
      }
      const intersectionArea = intersectionWidth * intersectionHeight;
      const elementArea = width * height;
      return elementArea > 0 && intersectionArea / elementArea >= ratio;
    }
    const isWithinX = left >= rootRect.left && right <= rootRect.right;
    if (axis === "x") {
      return isWithinX;
    }
    const isWithinY = top >= rootRect.top && bottom <= rootRect.bottom;
    if (axis === "y") {
      return isWithinY;
    }
    return isWithinX && isWithinY;
  });
}
export function getIOSVersion() {
  const { userAgent } = navigator;
  const isIOS = /(iPhone|iPad)/i.test(userAgent);
  if (!isIOS) return null;
  const version = userAgent.match(/OS ([\d_]+)/)?.[1];
  const [major, minor] = version?.split("_") || [];
  if (!version || !major) return null;
  return {
    fullString: version.replace("_", "."),
    major: parseInt(major, 10),
    minor: minor ? parseInt(minor, 10) : 0
  };
}
function getCardsToAnimate(grid, cards) {
  if (!grid || !cards || cards.length === 0) return 0;
  const itemSample = cards[0];
  if (!itemSample) return 0;
  const gridRect = grid.getBoundingClientRect();
  const visibleArea = {
    top: Math.max(0, gridRect.top),
    bottom: Math.min(window.innerHeight, gridRect.bottom)
  };
  const visibleHeight = Math.round(visibleArea.bottom - visibleArea.top);
  if (visibleHeight <= 0) return 0;
  const cardSample = itemSample.querySelector("product-card");
  const gridStyle = getComputedStyle(grid);
  const galleryAspectRatio = cardSample?.refs?.cardGallery?.style.getPropertyValue("--gallery-aspect-ratio") || "";
  let aspectRatio = parseFloat(galleryAspectRatio) || 0.5;
  if (galleryAspectRatio?.includes("/")) {
    const [width = "1", height = "2"] = galleryAspectRatio.split("/");
    aspectRatio = parseInt(width, 10) / parseInt(height, 10);
  }
  const cardGap = parseInt(cardSample?.refs?.productCardLink?.style.getPropertyValue("--product-card-gap") || "") || 12;
  const gridGap = parseInt(gridStyle.getPropertyValue("--product-grid-gap")) || 12;
  const detailsSize = ((parseInt(gridStyle.fontSize) || 16) + 2) * 2;
  const isMobile = window.innerWidth < 750;
  const cardWidth = isMobile ? Math.round((gridRect.width - gridGap) / 2) : 100;
  const cardHeight = Math.round(cardWidth / aspectRatio) + cardGap + detailsSize;
  const columnsInGrid = isMobile ? 2 : Math.floor((gridRect.width + gridGap) / (cardWidth + gridGap));
  const rowsInGrid = Math.ceil((visibleHeight - gridGap) / (cardHeight + gridGap));
  return columnsInGrid * rowsInGrid;
}
export function preloadImage(src) {
  const image = new Image();
  image.src = src;
}
export class TextComponent extends HTMLElement {
  shimmer() {
    this.setAttribute("shimmer", "");
  }
}
if (!customElements.get("text-component")) {
  customElements.define("text-component", TextComponent);
}
export function resetShimmer(container = document.body) {
  const shimmer = container.querySelectorAll("[shimmer]");
  shimmer.forEach((item) => item.removeAttribute("shimmer"));
}
export function changeMetaThemeColor(color) {
  const metaThemeColor = document.head.querySelector('meta[name="theme-color"]');
  if (metaThemeColor && color) {
    metaThemeColor.setAttribute("content", color);
  }
}
export function getViewParameterValue() {
  return new URLSearchParams(window.location.search).get("view");
}
export function parseIntOrDefault(value, defaultValue) {
  if (value === null || value === void 0 || value === "") {
    return defaultValue;
  }
  const parsed = parseInt(value.toString());
  return isNaN(parsed) ? defaultValue : parsed;
}
class Scheduler {
  /** @type {Set<() => void>} */
  #queue = /* @__PURE__ */ new Set();
  /** @type {boolean} */
  #scheduled = false;
  /** @param {() => void} task */
  schedule = async (task) => {
    this.#queue.add(task);
    if (!this.#scheduled) {
      this.#scheduled = true;
      if (viewTransition.current) await viewTransition.current;
      requestAnimationFrame(this.flush);
    }
  };
  flush = () => {
    for (const task of this.#queue) {
      setTimeout(task, 0);
    }
    this.#queue.clear();
    this.#scheduled = false;
  };
}
export const scheduler = new Scheduler();
export function oncePerEditorSession(element, sessionKeyName, callback) {
  const isInThemeEditor = window.Shopify?.designMode;
  const shopifyEditorSectionId = JSON.parse(element.dataset.shopifyEditorSection || "{}").id;
  const shopifyEditorBlockId = JSON.parse(element.dataset.shopifyEditorBlock || "{}").id;
  const editorId = shopifyEditorSectionId || shopifyEditorBlockId;
  const uniqueSessionKey = `${sessionKeyName}-${editorId}`;
  if (isInThemeEditor && sessionStorage.getItem(uniqueSessionKey)) return;
  callback();
  if (isInThemeEditor) sessionStorage.setItem(uniqueSessionKey, "true");
  return;
}
export class ResizeNotifier extends ResizeObserver {
  #initialized = false;
  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }
  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}
export function setHeaderMenuStyle() {
  const headerComponent = (
    /** @type {HTMLElement} | null */
    document.querySelector("#header-component")
  );
  if (headerComponent) {
    window.requestAnimationFrame(() => {
      const overflowList = headerComponent?.querySelector("overflow-list");
      const hasReachedMinimum = overflowList && overflowList.hasAttribute("minimum-reached");
      headerComponent.dataset.menuStyle = isTouchDevice() || hasReachedMinimum ? "drawer" : "menu";
    });
  }
}
export function calculateHeaderGroupHeight(header = document.querySelector("#header-component"), headerGroup = document.querySelector("#header-group")) {
  if (!headerGroup) return 0;
  let totalHeight = 0;
  for (const element of headerGroup.children) {
    if (element instanceof HTMLElement) totalHeight += element.offsetHeight;
  }
  if (header instanceof HTMLElement && header.hasAttribute("transparent") && header.parentElement?.nextElementSibling) {
    return totalHeight + header.offsetHeight;
  }
  return totalHeight;
}
function updateTransparentHeaderOffset() {
  const header = document.querySelector("#header-component");
  const headerGroup = document.querySelector("#header-group");
  const headerSection = headerGroup?.querySelector(".header-section");
  if (!headerSection || !header?.hasAttribute("transparent")) {
    document.body.style.setProperty("--transparent-header-offset-boolean", "0");
    return;
  }
  const hasImmediateSection = headerSection.nextElementSibling?.classList.contains("shopify-section");
  const shouldApplyOffset = !hasImmediateSection ? "1" : "0";
  document.body.style.setProperty("--transparent-header-offset-boolean", shouldApplyOffset);
}
function updateHeaderHeights() {
  const header = document.querySelector("header-component");
  if (!(header instanceof HTMLElement)) return;
  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);
  const headerTopRow = (
    /** @type {HTMLElement} | null */
    header.querySelector(".header__row--top")
  );
  document.body.style.setProperty("--header-height", `${headerHeight}px`);
  document.body.style.setProperty("--header-group-height", `${headerGroupHeight}px`);
  if (headerTopRow) {
    window.requestAnimationFrame(function() {
      header.style.setProperty("--top-row-height", `${headerTopRow.getBoundingClientRect().height}px`);
    });
  }
}
export function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
  setHeaderMenuStyle();
}
if (typeof Theme !== "undefined") {
  Theme.utilities = {
    ...Theme.utilities,
    scheduler
  };
}
